import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Dialog } from "radix-ui";
import { useBeliefMap, useDeleteAllBeliefs } from "#/api/beliefs";
import { useAuthUser } from "#/api/auth";
import { LogoMark } from "#/components/LogoMark";
import {
  Sparkles,
  ArrowLeft,
  Flame,
  AlertTriangle,
  Trash2,
  X,
} from "lucide-react";

const CONFIRMATION_PHRASE = "delete my beliefs";

export const Route = createFileRoute("/beliefs")({
  component: BeliefsPage,
});

function BeliefsPage() {
  const { data: user } = useAuthUser();
  const { data, isPending, isError } = useBeliefMap();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <LogoMark size="xl" />
        <h1 className="mt-6 mb-3 font-serif text-3xl font-light text-foreground">
          Ineya
        </h1>
        <p className="mb-8 max-w-sm text-muted-foreground">
          Sign in to see the map of beliefs you've transformed.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-primary backdrop-blur-sm transition-all hover:bg-white/[0.14]"
        >
          Sign in
        </Link>
      </div>
    );
  }

  const map = data;
  const hasData = !!map && map.totalFears > 0;

  return (
    <div className="min-h-screen px-4 pb-[max(6rem,env(safe-area-inset-bottom))] pt-[calc(env(safe-area-inset-top,0px)+var(--nav-height)+1rem)] md:px-6 md:pb-24 md:pt-28">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to flow
        </Link>

        <header className="mb-12">
          <p className="mb-2 text-xs uppercase tracking-widest text-primary/80">
            Your belief constellation
          </p>
          <h1 className="font-serif text-4xl font-light leading-tight text-foreground md:text-5xl">
            The stories behind your fears
          </h1>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground">
            Every fear you've walked through left a belief behind it. One belief
            often produces many fears — when you see the pattern, dismantling it
            once dissolves all of them.
          </p>
        </header>

        {isPending ? (
          <div className="flex items-center gap-2 text-primary/80">
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary" />
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:0.1s]" />
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:0.2s]" />
          </div>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            We couldn't load your belief map. Please try again.
          </p>
        ) : !hasData ? (
          <EmptyState />
        ) : (
          <>
            <Stats map={map} />
            <div className="mt-10 space-y-6">
              {map.clusters.map((cluster) => (
                <ClusterCard
                  key={cluster.limitingBelief.id}
                  cluster={cluster}
                />
              ))}
            </div>
            <div className="mt-16 border-t border-white/10 pt-8">
              <DeleteDataSection onOpen={() => setShowDeleteDialog(true)} />
            </div>
          </>
        )}

        <DeleteConfirmationDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
        />
      </div>
    </div>
  );
}

function Stats({ map }: { map: NonNullable<ReturnType<typeof useBeliefMap>["data"]> }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <Stat label="Fears owned" value={map.totalFears} />
      <Stat label="Beliefs seen through" value={map.totalLimitingBeliefs} />
      <Stat label="New beliefs chosen" value={map.totalEmpoweringBeliefs} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-center">
      <p className="font-serif text-3xl font-light text-primary">{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

function ClusterCard({
  cluster,
}: {
  cluster: NonNullable<ReturnType<typeof useBeliefMap>["data"]>["clusters"][number];
}) {
  const { limitingBelief, empoweringBelief, fears, fearCount } = cluster;
  const hero = fearCount >= 2;

  return (
    <article
      className={`rounded-3xl border p-6 backdrop-blur-sm ${
        hero
          ? "border-primary/30 bg-primary/[0.06]"
          : "border-white/10 bg-white/[0.04]"
      }`}
    >
      {hero && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-widest text-primary/90">
          <Flame className="h-3 w-3" />
          Behind {fearCount} fears
        </div>
      )}

      <div className="space-y-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            The story
          </p>
          <p className="mt-1 font-serif text-xl text-foreground/60 line-through decoration-primary/40">
            {limitingBelief.text}
          </p>
        </div>

        {empoweringBelief && (
          <div>
            <p className="text-xs uppercase tracking-widest text-primary/80">
              You chose instead
            </p>
            <p className="mt-1 font-serif text-2xl text-primary">
              {empoweringBelief.text}
            </p>
            {empoweringBelief.mantra && (
              <p className="mt-2 font-serif text-base italic text-primary/80">
                "{empoweringBelief.mantra}"
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 border-t border-white/10 pt-4">
        <p className="mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          {fearCount === 1 ? "The fear that knocked" : `The ${fearCount} fears it wore`}
        </p>
        <ul className="space-y-1.5">
          {fears.map((fear) => (
            <li key={fear.id} className="text-sm leading-relaxed text-foreground/80">
              — {fear.text}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function DeleteDataSection({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="rounded-2xl border border-destructive/20 bg-destructive/[0.04] p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-foreground">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Delete your saved data
          </h3>
          <p className="mt-1 max-w-md text-xs text-muted-foreground">
            This permanently removes all your saved beliefs and fears. Your
            account and session history will remain.
          </p>
        </div>
        <button
          onClick={onOpen}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive transition-all hover:bg-destructive/10 sm:flex-shrink-0"
        >
          <Trash2 className="h-4 w-4" />
          Delete all my data
        </button>
      </div>
    </div>
  );
}

function DeleteConfirmationDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [phrase, setPhrase] = useState("");
  const deleteMutation = useDeleteAllBeliefs();

  const phraseMatches = phrase.trim().toLowerCase() === CONFIRMATION_PHRASE;

  async function handleDelete() {
    if (!phraseMatches) return;
    await deleteMutation.mutateAsync();
    setPhrase("");
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-70 bg-black/60 backdrop-blur-sm transition-opacity data-[state=open]:opacity-100 data-[state=closed]:opacity-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-70 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 transition-all data-[state=open]:opacity-100 data-[state=closed]:opacity-0 data-[state=open]:scale-100 data-[state=closed]:scale-95">
          <div className="relative overflow-hidden rounded-2xl border border-destructive/30 bg-popover p-6 shadow-[0_0_40px_rgba(0,0,0,0.4)]">
            <Dialog.Close asChild>
              <button
                className="absolute right-4 top-4 rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>

            <div className="mb-5 flex flex-col items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-destructive/30 bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <p className="mt-4 text-xs uppercase tracking-widest text-destructive/80">
                This cannot be undone
              </p>
              <h2 className="mt-2 text-center font-serif text-2xl font-light text-foreground">
                Delete all your saved data?
              </h2>
            </div>

            <p className="mb-5 text-center text-sm leading-relaxed text-muted-foreground">
              You are about to permanently delete every saved belief and fear
              from your constellation. Your account and session history will not
              be affected.
            </p>

            <div className="mb-5 space-y-2">
              <label
                htmlFor="confirm-delete"
                className="block text-xs text-muted-foreground"
              >
                Type "{CONFIRMATION_PHRASE}" to confirm
              </label>
              <input
                id="confirm-delete"
                type="text"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={CONFIRMATION_PHRASE}
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-destructive/50 focus:outline-none"
                disabled={deleteMutation.isPending}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && phraseMatches) {
                    handleDelete();
                  }
                }}
              />
            </div>

            <button
              onClick={handleDelete}
              disabled={!phraseMatches || deleteMutation.isPending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-destructive px-6 py-3 text-sm font-medium text-destructive-foreground transition-all hover:bg-destructive/90 disabled:opacity-40 disabled:hover:bg-destructive"
            >
              {deleteMutation.isPending ? (
                "Deleting..."
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete all my data
                </>
              )}
            </button>

            {deleteMutation.isError && (
              <p className="mt-4 text-center text-xs text-destructive-foreground">
                {deleteMutation.error.message}
              </p>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center">
      <div className="mb-5 flex justify-center">
        <LogoMark size="lg" />
      </div>
      <h2 className="mb-3 font-serif text-2xl font-light text-foreground">
        Your map begins with one fear
      </h2>
      <p className="mx-auto mb-8 max-w-md text-sm leading-relaxed text-muted-foreground">
        Walk through your first transformation and the constellation will start
        to take shape — each fear you own reveals the belief behind it.
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-primary backdrop-blur-sm transition-all hover:bg-white/[0.14]"
      >
        <Sparkles className="h-4 w-4" />
        Begin transformation
      </Link>
    </div>
  );
}
